import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { candidateApi } from '@/api/candidateApi';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import type { CreateCandidateProfileRequest, CandidateFilter } from '@/types/candidates/candidates.types';
import { QK } from '@/hooks/queryKeys';
import { getApiErrorMessage } from '@/utils';

export function useCandidateProfiles(filters?: CandidateFilter) {
  return useQuery({
    queryKey: [...QK.CANDIDATE_LIST, filters],
    queryFn: () => candidateApi.getList(filters),
    staleTime: 1000 * 60 * 3,
  });
}

export function useGuestCandidateProfiles(filters?: CandidateFilter) {
  return useQuery({
    queryKey: QK.GUEST_CANDIDATE_LIST(filters),
    queryFn: () => candidateApi.getGuestList(filters),
    staleTime: 1000 * 60 * 3,
  });
}

export function useCandidateProfileByAccount(accountId: string) {
  return useQuery({
    queryKey: [...QK.CANDIDATE_DETAIL(accountId), 'by-account'],
    queryFn: () => candidateApi.getByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useMyCandidateProfile() {
  const { user } = useAuthStore();
  return useCandidateProfileByAccount(user?.id || '');
}


export function useCandidateProfile(id: string) {
  return useQuery({
    queryKey: QK.CANDIDATE_DETAIL(id),
    queryFn: () => candidateApi.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateCandidateProfile() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: CreateCandidateProfileRequest) => candidateApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidate-profiles'] });
      addToast('success', 'Tạo hồ sơ ứng viên thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Tạo hồ sơ thất bại')),
  });
}

export function useUpdateCandidateProfile() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateCandidateProfileRequest> }) => candidateApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidate-profiles'] });
      addToast('success', 'Cập nhật hồ sơ thành công!');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Cập nhật thất bại')),
  });
}

export function useHiddenFrom(id: string) {
  return useQuery({
    queryKey: [...QK.CANDIDATE_DETAIL(id), 'hidden-from'],
    queryFn: () => candidateApi.getHiddenFrom(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useHideCandidateProfile() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: ({ id, recruiterId }: { id: string; recruiterId: string }) => candidateApi.hideFrom(id, recruiterId),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: [...QK.CANDIDATE_DETAIL(id), 'hidden-from'] });
      addToast('success', 'Đã ẩn hồ sơ với nhà tuyển dụng này');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Ẩn hồ sơ thất bại')),
  });
}

export function useUnhideCandidateProfile() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: ({ id, recruiterId }: { id: string; recruiterId: string }) => candidateApi.unhideFrom(id, recruiterId),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: [...QK.CANDIDATE_DETAIL(id), 'hidden-from'] });
      addToast('success', 'Đã bỏ ẩn hồ sơ với nhà tuyển dụng này');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Bỏ ẩn hồ sơ thất bại')),
  });
}

import { recruiterNoteApi, RecruiterNotePayload } from '@/api/recruiterNoteApi';

export function useCreateRecruiterNote() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: (data: RecruiterNotePayload) => recruiterNoteApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.CANDIDATE_LIST });
      qc.invalidateQueries({ queryKey: ['recruiter-offers'] });
      addToast('success', 'Đã lưu ghi chú ứng viên');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Lỗi khi lưu ghi chú')),
  });
}

export function useUpdateRecruiterNote() {
  const qc = useQueryClient();
  const { addToast } = useUIStore();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RecruiterNotePayload> }) => recruiterNoteApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.CANDIDATE_LIST });
      qc.invalidateQueries({ queryKey: ['recruiter-offers'] });
      addToast('success', 'Đã cập nhật ghi chú ứng viên');
    },
    onError: (err: any) => addToast('error', getApiErrorMessage(err, 'Lỗi khi cập nhật ghi chú')),
  });
}
